import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import { useResizingHeight } from '@atlaskit/motion';
import { Flex, xcss } from '@atlaskit/primitives';
import { ReactRenderer } from '@atlaskit/renderer';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import { AIAgentResponseState, type ErrorMessage } from '@atlassian/ai-agent-streaming';
import { useUserPreferencesValue } from '@atlassian/jira-issue-user-preference-services/src/main.tsx';
import { PROACTIVE_COMMENT_SUMMARY_ENABLED } from '@atlassian/jira-issue-shared-types/src/common/types/user-preferences-type.tsx';
import { encodeAdf } from '@atlassian/conversation-assistant-content-renderer';
import AIContainer from '@atlassian/jira-atlassian-intelligence/src/common/ui/ai-container/index.tsx';
import { useIsAiEnabledForIssue } from '@atlassian/jira-issue-context-service/src/main.tsx';
import { useSmartSummarizeCommentsAnalytics } from '@atlassian/jira-issue-smart-request-summary-state/src/common/analytics/index.tsx';
import {
	useActions,
	useHasTriggeredNonProactive,
	useHasTriggeredProactive,
	useHasValidProactiveResponse,
	useIsGenerating,
	useIsPanelShown,
} from '@atlassian/jira-issue-smart-request-summary-state/src/controllers/state/index.tsx';
import { useSmartSummaryStreaming } from '@atlassian/jira-issue-smart-request-summary-state/src/services/convo-ai-streaming/index.tsx';
import {
	onLinkClick,
	smartLinksDefault,
} from '@atlassian/jira-linking-platform-utils/src/index.tsx';
import { useIsPremium } from '@atlassian/jira-platform-react-hooks-use-ai-opt-in/src/index.tsx';
import { FireScreenAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { useProfileCardProvider } from '@atlassian/jira-profilecard-provider/src/main.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import { useSelector } from '@atlassian/jira-react-redux/src/index.tsx';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { totalCommentsSelector } from '@atlassian/jira-issue-view-store/src/selectors/comment-selector.tsx';
import { useLocale } from '@atlassian/jira-tenant-context-controller/src/components/locale/index.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { SummaryNonProactive as SummaryNonProactiveNew } from './index-new.tsx';
import { FooterStreaming } from './footer/index.tsx';
import { GenerationSkeleton } from './footer/skeleton/index.tsx';
import { Header } from './header/index.tsx';
import { Container, ContentWrapper } from './styled.tsx';
import { mapMessageTemplateErrorToAdfError } from './utils.tsx';
import { ProactiveSummary } from './proactive.tsx';
import { PROACTIVE_COMMENT_THRESHOLD } from './constant.tsx';

export const SummaryNonProactiveOld = () => {
	const isAiOptInEnabled = useIsAiEnabledForIssue();
	const isPremium = useIsPremium();
	const isFeatureEnabled = isPremium && isAiOptInEnabled;
	const [isPanelShown] = useIsPanelShown();
	const { closeSummaryPanel, stopLoading, setIsGenerating } = useActions();
	const [isGenerating] = useIsGenerating();
	const { fireTrack } = useSmartSummarizeCommentsAnalytics();
	const dataProviders = new ProviderFactory();
	const profileCardProvider = useProfileCardProvider();
	dataProviders.setProvider('profilecardProvider', profileCardProvider);

	const [{ content, responseState, error }] = useSmartSummaryStreaming();

	const onContainerRef = useCallback((element: HTMLDivElement | null) => {
		if (element && typeof element.scrollIntoView === 'function') {
			element.scrollIntoView({
				behavior: 'smooth',
				block: 'center',
			});
		}
	}, []);

	const onClosePanel = useCallback(() => {
		closeSummaryPanel();
		/** - isGenerating refers to when we call stream endpoint before any events come back *
		 *  - initialization refers to initial setup of streaming
		 *  - answer part means still streaming response
		 * */
		const isStreaming =
			isGenerating ||
			responseState === AIAgentResponseState.AnswerPart ||
			responseState === AIAgentResponseState.Initialization;
		if (isStreaming) {
			stopLoading();
			fireTrack('aiInteraction dismissed', 'cancelJiraSmartSummarizeComments', {
				isGenerating: isStreaming,
			});
		}
	}, [closeSummaryPanel, isGenerating, responseState, stopLoading, fireTrack]);

	const resizingHeightProps = useResizingHeight({
		duration: () => 1500,
	});

	const adfNode = useMemo(() => {
		if (!content) {
			return null;
		}
		return encodeAdf(content, true);
	}, [content]);

	useEffect(() => {
		/**
		 * isGenerating refers to fetch stream call until we get some sort of content
		 * 	that is not initialisation (initialisation is basically no content). This means we actually have content of
		 * 	either streamed responses (answer part), final response (cached summaries)
		 * 	or error.
		 */
		if (
			isGenerating &&
			(responseState === AIAgentResponseState.AnswerPart ||
				responseState === AIAgentResponseState.FinalResponse ||
				responseState === AIAgentResponseState.Error)
		) {
			setIsGenerating(false);
		}
	}, [isGenerating, responseState, setIsGenerating]);

	if (!isFeatureEnabled || !isPanelShown) {
		return null;
	}

	const renderContent = () => {
		if (!adfNode) {
			return null;
		}

		return (
			<ReactRenderer
				eventHandlers={{
					link: { onClick: onLinkClick },
				}}
				document={adfNode}
				dataProviders={dataProviders}
				smartLinks={smartLinksDefault}
			/>
		);
	};

	const renderError = (errorMessageTemplate: ErrorMessage) => {
		return (
			<ReactRenderer
				eventHandlers={{
					link: { onClick: onLinkClick },
				}}
				document={mapMessageTemplateErrorToAdfError(errorMessageTemplate)}
				dataProviders={dataProviders}
				smartLinks={smartLinksDefault}
			/>
		);
	};

	const renderSummary = () => {
		switch (responseState) {
			case AIAgentResponseState.Error:
				return renderError(error?.message.message_template);
			case AIAgentResponseState.AnswerPart:
			case AIAgentResponseState.FinalResponse:
				return renderContent();
			default:
				return null;
		}
	};

	const renderSummaryComponent = () => {
		if (isGenerating) {
			return (
				<>
					<UFOLoadHold name="ai-smart-summaries.ui.first-stream-loading" />
					<Flex xcss={containerStyle}>
						<GenerationSkeleton />
					</Flex>
				</>
			);
		}

		return (
			<>
				<ContentWrapper {...resizingHeightProps} hasContent>
					{renderSummary()}
				</ContentWrapper>
				{responseState === AIAgentResponseState.Error ||
				responseState === AIAgentResponseState.FinalResponse ? (
					<FooterStreaming />
				) : null}
			</>
		);
	};

	return (
		<Container ref={onContainerRef}>
			<AIContainer
				isLoading={isGenerating}
				shouldFitContainer
				spacing="none"
				testId="issue-smart-request-summary.ui.ai-container"
				hasNewBorderExperience
			>
				<Header onClose={onClosePanel} />
				{renderSummaryComponent()}
			</AIContainer>
			<FireScreenAnalytics />
		</Container>
	);
};

export const SummaryNonProactive = componentWithCondition(
	() => expVal('jira_smart_summary_convo_ai_migration', 'isEnabled', false),
	SummaryNonProactiveNew,
	SummaryNonProactiveOld,
);

export const SummaryWithProactive = () => {
	const [hasTriggeredProactive] = useHasTriggeredProactive();
	const [hasValidProactiveResponse] = useHasValidProactiveResponse();
	const [hasTriggeredNonProactive] = useHasTriggeredNonProactive();
	const [isPanelShown] = useIsPanelShown();
	const isAiOptInEnabled = useIsAiEnabledForIssue();
	const isPremium = useIsPremium();
	const isFeatureEnabled = isPremium && isAiOptInEnabled;

	if (!isFeatureEnabled) {
		return null;
	}

	// non-proactive experience, this means they have to have pressed the ai button
	// if pressed before our proactive is generated, always show the non-proacitve
	// as we want to count that analytic event as a non-proactive case
	if (isPanelShown) {
		return <SummaryNonProactive />;
	}

	// if we have not triggered proactive before, or we have already triggered proactive and has a valid response
	// render the proactive component
	// also handles the case where, users haven't scrolled and press the summarise comment
	// from the dropdown menu. This means they have triggered the non proactive experience
	// so dont trigger the proactive case
	if ((!hasTriggeredProactive || hasValidProactiveResponse) && !hasTriggeredNonProactive) {
		return <ProactiveSummary />;
	}

	return null;
};

// we need to have it this way as our experiment is to compare ppl with 5+ comments and english locale
// hence the need to get this data first before we fire off the correct statsig exposure
export const SummaryWithExperimentOld = () => {
	const totalComments = useSelector(totalCommentsSelector);
	const locale = useLocale();
	const isEnglishLocale = locale && locale.startsWith('en');
	const [isProactiveExperimentConditionMet, setIsProactiveExperimentConditionMet] = useState(false);
	const isAiOptInEnabled = useIsAiEnabledForIssue();
	const isPremium = useIsPremium();
	const isFeatureEnabled = isPremium && isAiOptInEnabled;
	const [isPanelShown] = useIsPanelShown();
	const accountId = useAccountId();
	const [hasProactiveFeatureEnabled] = useUserPreferencesValue(PROACTIVE_COMMENT_SUMMARY_ENABLED);

	/**
	 * Prevent experiment exposure from triggering for experiences like
	 * 4 initial comments, and then add a new comment and then they are part of test group
	 * and an exposure fires and triggers a proactive experiment.
	 */
	useEffect(() => {
		if (
			totalComments >= PROACTIVE_COMMENT_THRESHOLD &&
			isEnglishLocale &&
			hasProactiveFeatureEnabled === 'true'
		) {
			setIsProactiveExperimentConditionMet(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	if (!isFeatureEnabled || !accountId) {
		return null;
	}

	if (
		isProactiveExperimentConditionMet &&
		expVal('jira_proactive_comment_summary_experiment', 'isEnabled', false)
	) {
		return <SummaryWithProactive />;
	}

	if (isPanelShown) {
		return <SummaryNonProactive />;
	}

	return null;
};

export const SummaryWithExperimentNew = ({
	isProactiveExperimentConditionMet = false,
}: {
	isProactiveExperimentConditionMet?: boolean;
}) => {
	const [isPanelShown] = useIsPanelShown();

	if (
		isProactiveExperimentConditionMet &&
		expVal('jira_proactive_comment_summary_experiment', 'isEnabled', false)
	) {
		return <SummaryWithProactive />;
	}

	if (isPanelShown) {
		return <SummaryNonProactive />;
	}

	return null;
};

export const SummaryWithExperiment = componentWithFG(
	'improve_issue_view_comment_ai',
	SummaryWithExperimentNew,
	SummaryWithExperimentOld,
);

export const Summary = componentWithFG(
	'proactive_comment_summary_targeting_gate',
	SummaryWithExperiment,
	SummaryNonProactive,
);

const containerStyle = xcss({
	marginBlockStart: 'space.150',
});
